import React, { useEffect, useState } from 'react';
import { useOrderNotification } from './OrderNotificationProvider';
import { FiBell, FiAlertOctagon } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

export default function NewOrderPopup() {
    const { newOrder, pendingCount, dismissPopup } = useOrderNotification();
    const navigate = useNavigate();
    const [now, setNow] = useState(Date.now());

    // Loop do som do alarme dinâmico baseado na prioridade
    useEffect(() => {
        if (!newOrder) return;
        
        let timeoutId;
        const scheduleTone = () => {
            const createdAt = new Date(newOrder.createdAt || newOrder.dataCriacao || Date.now());
            const currentDiffMins = Math.max(0, Math.floor((Date.now() - createdAt.getTime()) / 60000));
            
            try {
                const AudioContext = window.AudioContext || window.webkitAudioContext;
                if (AudioContext) {
                    const ctx = new AudioContext();
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    
                    if (currentDiffMins > 120) {
                        // Vermelho: Nítido e constante (square, mais alto e rápido)
                        osc.type = 'square';
                        osc.frequency.setValueAtTime(800, ctx.currentTime);
                        osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);
                        gain.gain.setValueAtTime(0, ctx.currentTime);
                        gain.gain.linearRampToValueAtTime(0.2, ctx.currentTime + 0.02);
                        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
                        osc.start(ctx.currentTime);
                        osc.stop(ctx.currentTime + 0.15);
                    } else if (currentDiffMins > 30) {
                        // Laranja: Grave (sawtooth)
                        osc.type = 'sawtooth';
                        osc.frequency.setValueAtTime(300, ctx.currentTime);
                        gain.gain.setValueAtTime(0, ctx.currentTime);
                        gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.05);
                        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
                        osc.start(ctx.currentTime);
                        osc.stop(ctx.currentTime + 0.4);
                    } else {
                        // Verde: Sutil (sine)
                        osc.type = 'sine';
                        osc.frequency.setValueAtTime(600, ctx.currentTime);
                        gain.gain.setValueAtTime(0, ctx.currentTime);
                        gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 0.05);
                        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
                        osc.start(ctx.currentTime);
                        osc.stop(ctx.currentTime + 0.3);
                    }
                }
            } catch(e) {}

            // Tempo entre toques muda com a prioridade
            const delay = currentDiffMins > 120 ? 800 : currentDiffMins > 30 ? 2000 : 4000;
            timeoutId = setTimeout(scheduleTone, delay);
        };

        scheduleTone();

        return () => clearTimeout(timeoutId);
    }, [newOrder]);

    // Relógio em tempo real para o cronômetro do Limbo
    useEffect(() => {
        if (!newOrder) return;
        const clockId = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(clockId);
    }, [newOrder]);

    if (!newOrder) return null;

    const handleOpenOrder = () => {
        dismissPopup();
        navigate('/pedidos/triagem');
    };

    const createdAt = new Date(newOrder.createdAt || newOrder.dataCriacao || Date.now());
    const diffMs = now - createdAt.getTime();
    const diffMins = Math.max(0, Math.floor(diffMs / 60000));
    const diffSecs = Math.max(0, Math.floor((diffMs % 60000) / 1000));

    let colors = {
        bgPulse: "bg-emerald-500/20",
        borderBox: "border-emerald-500",
        shadowBox: "shadow-[0_0_100px_rgba(16,185,129,0.5)]",
        iconBg: "bg-emerald-500/10 border-emerald-500/30",
        textCol: "text-emerald-500",
        btnBox: "bg-emerald-600 hover:bg-emerald-500 shadow-[0_0_30px_rgba(16,185,129,0.4)]"
    };

    if (diffMins > 120) {
        colors = {
            bgPulse: "bg-red-500/20",
            borderBox: "border-red-500",
            shadowBox: "shadow-[0_0_100px_rgba(239,68,68,0.5)]",
            iconBg: "bg-red-500/10 border-red-500/30",
            textCol: "text-red-500",
            btnBox: "bg-red-600 hover:bg-red-500 shadow-[0_0_30px_rgba(239,68,68,0.4)]"
        };
    } else if (diffMins > 30) {
        colors = {
            bgPulse: "bg-orange-500/20",
            borderBox: "border-orange-500",
            shadowBox: "shadow-[0_0_100px_rgba(249,115,22,0.5)]",
            iconBg: "bg-orange-500/10 border-orange-500/30",
            textCol: "text-orange-500",
            btnBox: "bg-orange-600 hover:bg-orange-500 shadow-[0_0_30px_rgba(249,115,22,0.4)]"
        };
    }

    const totalMonthly = newOrder.itens?.reduce((acc, it) => {
        return acc + ((it.quantidade || 0) * (it.precoUnitario || 0));
    }, 0) || 0;

    return (
        <div className="fixed inset-0 z-[999999] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md overflow-hidden">
            {/* Efeito de Sirene (Piscar a Tela) */}
            <div className={`absolute inset-0 animate-pulse pointer-events-none ${colors.bgPulse}`} />

            <div className={`relative z-10 bg-black border p-8 rounded-[3rem] flex flex-col items-center text-center max-w-md w-full animate-in zoom-in duration-300 ${colors.borderBox} ${colors.shadowBox}`}>
                <div className={`p-6 rounded-full mb-6 animate-pulse border ${colors.iconBg}`}>
                    <FiAlertOctagon size={64} className={colors.textCol} />
                </div>
                
                <h1 className={`text-3xl font-black uppercase tracking-widest mb-2 ${colors.textCol}`}>
                    {pendingCount > 1 ? `Temos ${pendingCount} pedidos novos!` : `Temos ${pendingCount || 1} pedido novo!`}
                </h1>
                
                <p className="text-gray-400 text-sm font-bold uppercase tracking-widest mb-2">
                    E o mais recente é do Cliente:
                    <span className="text-white text-xl mt-2 block break-words max-w-full">
                        {newOrder.empresa?.razaoSocial || newOrder.clienteNome || 'Cliente Desconhecido'}
                    </span>
                    <span className={`block mt-2 text-[10px] opacity-70 font-mono tracking-widest ${colors.textCol}`}>
                        Tempo no Limbo: {String(diffMins).padStart(2, '0')}:{String(diffSecs).padStart(2, '0')}
                    </span>
                </p>

                {totalMonthly > 0 && (
                    <div className="mb-6 px-6 py-2 border border-green-500/30 rounded-2xl bg-green-500/10">
                        <span className="text-[10px] font-black uppercase tracking-widest text-green-500 opacity-60 block mb-1">Receita Mensal Prevista</span>
                        <span className="text-2xl font-black text-green-400">
                            {totalMonthly.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </span>
                    </div>
                )}

                <button 
                    onClick={handleOpenOrder}
                    className={`w-full text-white font-black text-sm uppercase tracking-[0.2em] py-5 px-8 rounded-2xl flex items-center justify-center gap-3 transition-all hover:scale-105 active:scale-95 ${colors.btnBox}`}
                >
                    <FiBell className="animate-bounce" size={20} />
                    Já vi e vou assumir
                </button>
            </div>
        </div>
    );
}
